import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { serverErrorText } from "./serverError";
import { StoredTemplateInvalidAlert } from "./StoredTemplateInvalidAlert";
import { StoredTemplateText } from "./StoredTemplateText";
import { TemplateField, isTemplateSubmittable } from "./TemplateField";
import { TemplateLegend } from "./TemplateLegend";
import { useOrgTemplate } from "./useOrgTemplate";
import { useRepoTemplate } from "./useRepoTemplate";
import { useResetRepoTemplate } from "./useResetRepoTemplate";
import { useSetRepoTemplate } from "./useSetRepoTemplate";
import type { BranchTemplateSettingsResponse } from "./api";

// Секция per-repo override. Репозиторий выбирается свободным вводом
// `owner/repo` (зеркало `ReviewerChecklistSection`): per-(org, repo)
// настройки не завязаны на состояние Forgejo-подключения, а связной
// эндпоинт списка подключённых репо требует активного коннекта и даёт
// 404 без него. Легенда/пресеты берутся из org-запроса (кэш react-query).
export function RepoTemplateSection(): ReactElement {
  const { t } = useTranslation();
  const settings = useOrgTemplate();
  const [repoFullName, setRepoFullName] = useState("");

  const trimmedRepo = repoFullName.trim();
  const repoEntered = trimmedRepo.length > 0;

  return (
    <Stack gap="md">
      <Title order={2}>{t("branchNameTemplate.repo.title")}</Title>
      <Text c="dimmed">{t("branchNameTemplate.repo.intro")}</Text>

      <TextInput
        label={t("branchNameTemplate.repo.repoLabel")}
        description={t("branchNameTemplate.repo.repoHint")}
        placeholder={t("branchNameTemplate.repo.repoPlaceholder")}
        value={repoFullName}
        onChange={(event) => {
          setRepoFullName(event.currentTarget.value);
        }}
      />

      {repoEntered && settings.data !== undefined ? (
        <RepoTemplateBody repoFullName={trimmedRepo} settings={settings.data} />
      ) : (
        <Text c="dimmed">{t("branchNameTemplate.repo.enterRepo")}</Text>
      )}
    </Stack>
  );
}

// Тело секции после ввода репо: грузит override репозитория, затем
// рендерит редактор. Loader/Error до загрузки; редактор перемонтируется
// по `key` (репо + сохранённое значение override) после save/clear.
// Ключ включает всё, от чего зависит начальное значение формы;
// `JSON.stringify` не склеивает `null` и пустую строку (она тоже может
// быть сохранённым невалидным значением).
function RepoTemplateBody({
  repoFullName,
  settings,
}: {
  readonly repoFullName: string;
  readonly settings: BranchTemplateSettingsResponse;
}): ReactElement {
  const { t } = useTranslation();
  const repo = useRepoTemplate(repoFullName);

  if (repo.isPending) {
    return <Loader />;
  }
  if (repo.isError) {
    return (
      <Alert color="red" title={t("branchNameTemplate.errorHeading")}>
        {t("branchNameTemplate.repo.loadingError")}
      </Alert>
    );
  }

  return (
    <RepoTemplateEditor
      key={JSON.stringify([repoFullName, repo.data.template])}
      repoFullName={repoFullName}
      override={repo.data.template}
      invalidReason={repo.data.template_invalid_reason}
      settings={settings}
    />
  );
}

// Редактор override'а с инициализированным draft'ом. При наследовании
// (override === null) draft предзаполнен org-эффективным шаблоном как
// стартовой точкой; иначе — текущим override'ом (в том числе невалидным —
// для правки). При невалидном override задачи репозитория откатываются на
// заводской шаблон (`default_template`), а не на `effective_org_template`.
function RepoTemplateEditor({
  repoFullName,
  override,
  invalidReason,
  settings,
}: {
  readonly repoFullName: string;
  readonly override: string | null;
  readonly invalidReason: string | null;
  readonly settings: BranchTemplateSettingsResponse;
}): ReactElement {
  const { t } = useTranslation();
  const setMutation = useSetRepoTemplate();
  const resetMutation = useResetRepoTemplate();
  const [draft, setDraft] = useState(
    override ?? settings.effective_org_template,
  );

  const hasOverride = override !== null;
  const submittable = isTemplateSubmittable(draft, settings.placeholders);
  const mutationError = setMutation.error ?? resetMutation.error;
  const serverError =
    mutationError !== null ? serverErrorText(mutationError, t) : null;

  return (
    <Stack gap="md">
      <Group gap="xs">
        <Text size="sm" fw={600}>
          {t("branchNameTemplate.repo.currentLabel")}
        </Text>
        {hasOverride ? (
          <StoredTemplateText value={override} />
        ) : (
          <Badge variant="light" color="gray">
            {t("branchNameTemplate.repo.inherits")}
          </Badge>
        )}
      </Group>

      {invalidReason !== null ? (
        <StoredTemplateInvalidAlert
          storedTemplate={null}
          reason={invalidReason}
          fallbackNote={t("branchNameTemplate.repo.invalidFallback", {
            template: settings.default_template,
          })}
        />
      ) : null}

      <TemplateField
        value={draft}
        onChange={setDraft}
        presets={settings.presets}
        placeholders={settings.placeholders}
        label={t("branchNameTemplate.repo.fieldLabel")}
        serverError={serverError}
        legendSlot={<TemplateLegend placeholders={settings.placeholders} />}
      />

      <Group>
        <Button
          onClick={() => {
            setMutation.mutate({
              repoFullName,
              body: { template: draft.trim() },
            });
          }}
          disabled={!submittable}
          loading={setMutation.isPending}
        >
          {t("branchNameTemplate.repo.saveButton")}
        </Button>
        <Button
          variant="default"
          onClick={() => {
            resetMutation.mutate(repoFullName);
          }}
          disabled={!hasOverride}
          loading={resetMutation.isPending}
        >
          {t("branchNameTemplate.repo.clearButton")}
        </Button>
      </Group>
    </Stack>
  );
}
